import React from "react"
import { Badge } from "../../components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { 
  RotateCcw, 
  Package, 
  CreditCard, 
  AlertTriangle, 
  Phone, 
  Mail,
  Calendar,
  Clock,
  MapPin,
  CheckCircle
} from "lucide-react"
import { useState, useEffect } from "react"
import SEO from "../SEO"

export default function RefundCancellation() {
  const refundCancellationSchema = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://pranaamhospitals.com/#organization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/",
    "logo": "https://pranaamhospitals.com/images/pranaam-logo.png",
    "description": "Read the Refund & Cancellation Policy of Pranaam Hospitals, including return timelines, refund process, eligibility, and customer support details for a smooth experience.",
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals",
      "https://www.linkedin.com/company/pranaamhospitals"
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": "https://pranaamhospitals.com/refund-cancellation#breadcrumb",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://pranaamhospitals.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Refund & Cancellation Policy",
        "item": "https://pranaamhospitals.com/refund-cancellation"
      }
    ]
  }
];
const [seo, setSeo] = useState({});
      const [pageKeywords, setPageKeywords] = useState("");
    const [pageData, setPageData] = useState(null);
    useEffect(() => {
      fetch("/wp-json/custom/v1/page-seo?slug=refund-cancellation")
      .then(res => res.json())
      .then(data => { if (data?.seo) setSeo(data.seo); setPageKeywords(data?.keywords || ""); })
      .catch(() => {});
    }, []);

  return (
    <>
      <SEO
        title={seo.title || "Refund & Cancellation Policy | Pranaam Hospitals"}
        description={seo.description || "Read the Refund & Cancellation Policy of Pranaam Hospitals to understand our policies regarding appointment cancellations, refunds, online payments, and healthcare service transactions."}
        canonical={seo.canonical || "https://pranaamhospitals.com/refund-cancellation"}
        robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
        keywords={pageKeywords}
        ogTitle={seo.og_title}
        ogDescription={seo.og_description}
        ogImage={seo.og_image || seo.featured_image}
        twitterTitle={seo.twitter_title}
        twitterDescription={seo.twitter_description}
        author="Pranaam Hospitals"
        publisher="Pranaam Hospitals"
        schema={refundCancellationSchema}
      />

      <main className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-primary/10 to-primary/5 py-16">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1">Patient Care</Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl">
                Refund & <span className="text-primary">Cancellation</span> Policy
              </h1>
              <div className="flex items-center justify-center gap-2 mb-6">
                <Calendar className="w-5 h-5 text-primary" />
                <p className="text-lg text-gray-700">Last updated: March 15, 2022</p>
              </div>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Thank you for your purchase. We hope you are happy with your purchase. However, if you are not completely satisfied with your purchase for any reason, you may return it to us for a full refund only.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto space-y-8">
              
              {/* Returns Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RotateCcw className="w-6 h-6 text-primary" />
                    Returns
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-blue-800">Return Timeline</h3>
                      </div>
                      <p className="text-blue-700">All returns must be postmarked within three (3) days of the purchase date.</p>
                    </div>
                    
                    <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-5 h-5 text-green-600" />
                        <h3 className="font-semibold text-green-800">Item Condition</h3>
                      </div>
                      <p className="text-green-700">All returned items must be in new and unused condition, with all original tags and labels attached.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Return Process */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-primary" />
                    Return Process
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-gray-700">
                    To return an item, please email customer service at 
                    <a href="mailto:purchase@pranaamhospitals.com"
                     className="text-primary underline hover:no-underline font-medium"
                     >
                      purchase@pranaamhospitals.com
                    </a> to obtain a Return Merchandise Authorization (RMA) number.
                  </p>
                  
                  <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-500">
                    <h3 className="font-semibold text-yellow-800 mb-4">Step-by-Step Return Process:</h3>
                    <ol className="space-y-3 text-yellow-700">
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-200 text-yellow-800 rounded-full flex items-center justify-center text-sm font-semibold">1</span>
                        <span>Email customer service to obtain RMA number</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-200 text-yellow-800 rounded-full flex items-center justify-center text-sm font-semibold">2</span>
                        <span>Place item securely in original packaging</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-200 text-yellow-800 rounded-full flex items-center justify-center text-sm font-semibold">3</span>
                        <span>Include your proof of purchase</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-200 text-yellow-800 rounded-full flex items-center justify-center text-sm font-semibold">4</span>
                        <span>Mail to the address provided below</span>
                      </li>
                    </ol>
                  </div>

                  {/* Return Address */}
                  <Card className="bg-gray-50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <MapPin className="w-5 h-5 text-primary" />
                        Return Address
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-gray-700">
                        <p className="font-semibold">At the place of Purchase</p>
                        <p>Attn: Returns</p>
                        <p>RMA #</p>
                        <p>1-56/6/40& 41, Pranaam Hospital Ln,</p>
                        <p>Mythri Nagar, Madeenaguda,</p>
                        <p>Telangana 500050</p>
                        <p>Hyderabad</p>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-green-700 font-medium">
                      <CheckCircle className="w-5 h-5 inline mr-2" />
                      Return shipping charges will be paid or reimbursed by us.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Refunds */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-6 h-6 text-primary" />
                    Refunds
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700">
                    After receiving your return and inspecting the condition of your item, we will process your return.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-semibold text-blue-800 mb-2">Processing Time</h3>
                      <p className="text-blue-700">Please allow at least seven (7) days from the receipt of your item to process your return.</p>
                    </div>
                    
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h3 className="font-semibold text-purple-800 mb-2">Credit Card Refunds</h3>
                      <p className="text-purple-700">Refunds may take 1-2 billing cycles to appear on your credit card statement, depending on your credit card company.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Exceptions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                    Exceptions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                    <h3 className="font-semibold text-red-800 mb-2">The following items cannot be returned:</h3>
                    <ul className="text-red-700">
                      <li>• GST</li>
                    </ul>
                  </div>
                  
                  <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                    <p className="text-orange-700">
                      <strong>For defective or damaged products:</strong> Please contact us at the contact details below to arrange a refund or exchange.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card className="border-l-4 border-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Phone className="w-6 h-6" />
                    Questions & Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-6">
                    If you have any questions concerning our return policy, please contact us at:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-lg">
                      <Phone className="w-6 h-6 text-primary flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-800">Phone</h3>
                        <a
                          href="tel:04023042777"
                          className="text-primary underline hover:no-underline font-medium"
                        >
                          040 23042777
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-lg">
                      <Mail className="w-6 h-6 text-primary flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-800">Email</h3>
                        <a href="mailto:purchase@pranaamhospitals.com" className="text-primary hover:underline">purchase@pranaamhospitals.com</a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>
      </main>
    </>
  )
}